import { MouseEvent, useCallback, useMemo } from 'react';
import NiceModal from '@ebay/nice-modal-react';
import { createEmailStatusColumns } from '@hooks/common/useDynamicColumns/types/emailStatusColumns';
import { createJobsColumns } from '@hooks/common/useDynamicColumns/types/jobsColumns';
import { createLocalSearchColumns } from '@hooks/common/useDynamicColumns/types/localSearchColumns';
import { createPropertyColumns } from '@hooks/common/useDynamicColumns/types/propertyColumns';
import { ScraperParams } from '@hooks/types';

import { GridColDef, GridRowParams } from '@mui/x-data-grid';
import { FullDetailsModalId } from '@components/FullDetailsModal';

// Map scraper/resource names to column definitions (lead generation focused)
// All scrapers produce contact data: name, email, phone
const campaignColumnsHashMap: Record<string, { columns: GridColDef[] }> = {
  // Job Sites - recruiter/company contact data
  indeed: { columns: createJobsColumns() },
  linkedin: { columns: createJobsColumns() },
  // Real Estate - agent/broker contact data
  realtor: { columns: createPropertyColumns() },
  zillow: { columns: createPropertyColumns() },
  renthop: { columns: createPropertyColumns() },
  realtyhop: { columns: createPropertyColumns() },
  streeteasy: { columns: createPropertyColumns() },
  hotpads: { columns: createPropertyColumns() },
  loopnet: { columns: createPropertyColumns() },
  craigslist: { columns: createPropertyColumns() },
  // Local Search - business contact data
  google: { columns: createLocalSearchColumns() },
  google_maps: { columns: createLocalSearchColumns() },
  yelp: { columns: createLocalSearchColumns() },
  yellowpages: { columns: createLocalSearchColumns() },
  tripadvisor: { columns: createLocalSearchColumns() },
  bing_maps: { columns: createLocalSearchColumns() },
  bing_places: { columns: createLocalSearchColumns() },
  // Social Media - profile contact data
  instagram: { columns: createLocalSearchColumns() },
  facebook_marketplace: { columns: createLocalSearchColumns() },
};

// Map campaign type names to column definitions (lead generation focused)
const campaignTypeColumnsHashMap: Record<string, { columns: GridColDef[] }> = {
  // Original mappings
  Jobs: { columns: createJobsColumns() },
  Properties: { columns: createPropertyColumns() },
  // Campaign type mappings (from database)
  'Local Search': { columns: createLocalSearchColumns() },
  'Real Estate': { columns: createPropertyColumns() },
  'Job Sites': { columns: createJobsColumns() },
  'Social Media': { columns: createLocalSearchColumns() },
  // Fallback variations (snake_case)
  local_search: { columns: createLocalSearchColumns() },
  real_estate: { columns: createPropertyColumns() },
  job_sites: { columns: createJobsColumns() },
  social_media: { columns: createLocalSearchColumns() },
};

const generateCampaignDetailsColumns = (resource: string): GridColDef[] => {
  // Normalize resource name: lowercase, replace spaces and hyphens with underscores
  const resourceKey = resource.toLowerCase().replace(/[-\s]+/g, '_');
  return campaignColumnsHashMap[resourceKey]?.columns || createLocalSearchColumns();
};

const generateDbColumns = (type: string): GridColDef[] => {
  // Try exact match first
  if (campaignTypeColumnsHashMap[type]) {
    return campaignTypeColumnsHashMap[type].columns;
  }
  // Try lowercase/normalized version
  const normalizedType = type.toLowerCase().replace(/ /g, '_');
  if (campaignTypeColumnsHashMap[normalizedType]) {
    return campaignTypeColumnsHashMap[normalizedType].columns;
  }
  // Default to Local Search columns as fallback
  return createLocalSearchColumns();
};

interface UseDynamicColumnsProps {
  source?: string;
  campaignType?: string;
  /**
   * When true, email status columns will be added to campaign details view.
   * Set this when the campaign has email integration enabled.
   */
  hasEmailIntegration?: boolean;
}

export const useDynamicColumns = ({ source, campaignType, hasEmailIntegration = false }: UseDynamicColumnsProps) => {
  // Get base columns based on source/scraper type
  const baseColumns: GridColDef[] = source ? generateCampaignDetailsColumns(source) : createLocalSearchColumns();

  // Memoize email columns to prevent recreating on every render
  const emailColumns = useMemo(() => createEmailStatusColumns(), []);

  // Combine base columns with email columns if integration is enabled
  const campaignDetailsColumns: GridColDef[] = useMemo(() => {
    if (hasEmailIntegration) {
      // Insert email columns after the email field column
      const emailFieldIndex = baseColumns.findIndex(col => col.field === 'email');
      if (emailFieldIndex !== -1) {
        return [
          ...baseColumns.slice(0, emailFieldIndex + 1),
          ...emailColumns,
          ...baseColumns.slice(emailFieldIndex + 1),
        ];
      }
      // If no email field found, append at the end
      return [...baseColumns, ...emailColumns];
    }
    return baseColumns;
  }, [baseColumns, emailColumns, hasEmailIntegration]);

  const dbColumns: GridColDef[] = campaignType ? generateDbColumns(campaignType) : createLocalSearchColumns();

  const onRowClick = useCallback((params: GridRowParams<ScraperParams>, event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    const { row } = params;
    NiceModal.show(FullDetailsModalId, { row });
  }, []);

  return { campaignDetailsColumns, onRowClick, dbColumns, emailColumns };
};
